import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './InfoTip.css';

const VIEWPORT_MARGIN = 8;
const GAP = 6;

// Below the "?" (above it when there is no room), centred on it but kept inside the viewport.
function placeBubble(button, bubble) {
  const anchor = button.getBoundingClientRect();
  const box = bubble.getBoundingClientRect();
  const viewportWidth = document.documentElement.clientWidth;
  const centred = anchor.left + anchor.width / 2 - box.width / 2;
  const left = Math.min(Math.max(centred, VIEWPORT_MARGIN), viewportWidth - VIEWPORT_MARGIN - box.width);
  const below = anchor.bottom + GAP;
  const above = anchor.top - GAP - box.height;
  const fitsBelow = below + box.height <= window.innerHeight - VIEWPORT_MARGIN;
  const top = fitsBelow || above < VIEWPORT_MARGIN ? below : above;
  return { left, top };
}

// A "?" explaining the text next to it: hover, keyboard focus or tap opens it; a tap pins it until a
// second tap, a tap outside or Esc. The bubble is fixed in <body> so nothing around the "?" clips it.
export default function InfoTip({ label, children }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);
  const bubbleRef = useRef(null);
  const id = useId();
  const open = hovered || focused || pinned;

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setHovered(false);
        setFocused(false);
        setPinned(false);
      }
    };
    const closeOnOutside = (event) => {
      if (!buttonRef.current.contains(event.target) && !bubbleRef.current.contains(event.target)) {
        setHovered(false);
        setPinned(false);
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    document.addEventListener('pointerdown', closeOnOutside);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.removeEventListener('pointerdown', closeOnOutside);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    const follow = () => setPosition(placeBubble(buttonRef.current, bubbleRef.current));
    follow();
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open]);

  const bubble = (
    <span
      id={id}
      role="tooltip"
      className="info-tip-bubble"
      ref={bubbleRef}
      hidden={!open}
      style={position ? { left: position.left, top: position.top } : { visibility: 'hidden' }}
    >
      {children}
    </span>
  );

  return (
    <span
      className="info-tip"
      onPointerEnter={(event) => event.pointerType === 'mouse' && setHovered(true)}
      onPointerLeave={(event) => event.pointerType === 'mouse' && setHovered(false)}
    >
      <button
        type="button"
        className="info-tip-button"
        ref={buttonRef}
        aria-label={label}
        aria-describedby={id}
        aria-expanded={open}
        onFocus={(event) => setFocused(event.target.matches(':focus-visible'))}
        onBlur={() => setFocused(false)}
        onClick={() => setPinned((wasPinned) => !wasPinned)}
      >
        ?
      </button>
      {ReactDOM.createPortal(bubble, document.body)}
    </span>
  );
}
